import { useNavigate } from "react-router-dom";
import { useCart } from "react-use-cart";
import { Plus, Minus, Trash2 } from "lucide-react";
import { formatPrice } from "../utils/helper";
import Hero from "../components/Hero";

export default function Cart() {
  const { cartTotal, items, updateItemQuantity, removeItem, emptyCart } =
    useCart();
  const navigate = useNavigate();

  const storedUser = JSON.parse(localStorage.getItem("user"));

  if (!storedUser) {
  return (
    <div className="min-h-screen bg-[#fef8f5] flex flex-col justify-center items-center text-center px-4">
      <h2 className="text-2xl font-semibold text-[#7a6e5f] mb-4">
        Please log in to view your cart 🛒
      </h2>
      <button
        onClick={() => navigate("/login")}
        className="bg-[#809080] text-white px-6 py-3 rounded-md hover:bg-[#6d7d6d]"
      >
        Go to Login
      </button>
    </div>
  );
}

  if (items.length === 0) {
    return (
      <div className="bg-[#fef8f5] min-h-screen">
        <Hero
          title={"Shopping Cart"}
          description={"Check out your cart"}
          image={
            "https://img.freepik.com/free-photo/pink-empty-shopping-basket-online-shopping-concept-pink-background-3d-rendering_56104-1527.jpg?t=st=1745421066~exp=1745424666~hmac=3445267cea69f41120ce7afa070e973b1a8955beabbe8364782953d057520487&w=900"
          }
        />
        <div className="max-w-7xl mx-auto px-4 py-16 text-center">
          <div className="flex items-center justify-center">
            <img src="/empty_cart.png" alt="Empty cart" width={400} />
          </div>
          <h2 className="text-2xl font-bold mb-4 text-[#7a6e5f]">
            Your cart is empty
          </h2>
          <button
            onClick={() => navigate("/products")}
            className="mt-6 bg-[#809080] text-white px-6 py-3 rounded-md hover:bg-[#6d7d6d]"
          >
            Shop Now
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#fef8f5] min-h-screen">
      <Hero
        title={"Shopping Cart"}
        description={"Check out your cart and make your skin happy."}
        image={
          "https://img.freepik.com/free-photo/pink-empty-shopping-basket-online-shopping-concept-pink-background-3d-rendering_56104-1527.jpg?t=st=1745421066~exp=1745424666~hmac=3445267cea69f41120ce7afa070e973b1a8955beabbe8364782953d057520487&w=900"
        }
      />

      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-[#7a6e5f]">Shopping Cart</h1>
          <button
            onClick={emptyCart}
            className="text-sm text-red-500 hover:underline"
          >
            Remove All Items
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="md:col-span-2 space-y-6">
            {items.map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-4 bg-white rounded-lg p-4 shadow-sm border border-[#f5eee6]"
              >
                <img
                  src={
                    item.image
                      ? `${process.env.REACT_APP_API_URL}/storage/${item.image}`
                      : "https://via.placeholder.com/80x80?text=No+Image"
                  }
                  alt={item.name}
                  className="w-20 h-20 object-cover rounded-md"
                />

                <div className="flex-1 flex justify-between items-center">
                  <div>
                    <h3 className="font-semibold text-[#7a6e5f] text-base">
                      {item.name}
                    </h3>

                    {item.variant_name && (
                      <p className="text-gray-600">{item.variant_name}</p>
                    )}

                    <p className="text-sm text-gray-500">
                      {formatPrice(item.price)}
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={() =>
                        updateItemQuantity(
                          item.id,
                          Math.max(0, item.quantity - 1)
                        )
                      }
                      className="p-2 hover:bg-[#fef8f5] rounded-full"
                    >
                      <Minus className="w-4 h-4 text-[#809080]" />
                    </button>

                    <span className="w-6 text-center font-medium">
                      {item.quantity}
                    </span>

                    <button
                      onClick={() =>
                        updateItemQuantity(item.id, item.quantity + 1)
                      }
                      className="p-2 hover:bg-[#fef8f5] rounded-full"
                    >
                      <Plus className="w-4 h-4 text-[#809080]" />
                    </button>

                    <button
                      onClick={() => removeItem(item.id)}
                      className="p-2 text-red-500 hover:bg-red-100 rounded-full"
                      aria-label="Remove item"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="bg-white p-6 rounded-lg shadow-md h-fit">
            <h3 className="text-xl font-semibold text-[#7a6e5f] mb-4">
              Order Summary
            </h3>

            <div className="space-y-2 mb-4 text-sm text-gray-600">
              {items.map((item) => (
                <div key={item.id} className="flex justify-between">
                  <span>
                    {item.name} × {item.quantity}
                  </span>
                  <span>{formatPrice(item.price * item.quantity)}</span>
                </div>
              ))}
            </div>

            <div className="border-t pt-4 flex justify-between font-semibold text-[#7a6e5f]">
              <span>Total</span>
              <span>{formatPrice(cartTotal)}</span>
            </div>

            {storedUser ? (
              <button
                onClick={() => navigate("/checkout")}
                className="w-full bg-[#809080] text-white py-3 rounded-md mt-6 hover:bg-[#6d7d6d]"
              >
                Proceed to Checkout
              </button>
            ) : (
              <button onClick={() => navigate("/login")}>
                Login to Checkout
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
